import { API_BASE_URL } from './api';

/**
 * Shared low-level `fetch` wrapper for the ~20 call sites that need the raw
 * `Response` (CSV/ICS blob downloads, ad-hoc JSON endpoints not covered by the
 * typed `apiService`). It exists to fix audit finding #12: bare
 * bare relative fetch calls (to /api/... paths) have no base URL, so in the
 * statically-served
 * production build they hit the SPA origin (the static site) and get
 * `index.html` back instead of
 * the backend.
 *
 * Contract:
 *   - Prepends the SINGLE base-URL source used by the axios client
 *     (`API_BASE_URL`, exported from ./api) so there is exactly one place that
 *     reads `REACT_APP_API_URL`.
 *   - Attaches the bearer token the same way the existing bare-fetch call sites
 *     did (localStorage, falling back to sessionStorage).
 *   - Returns the raw `fetch` Response — callers keep their existing
 *     `.ok` / `.json()` / `.blob()` handling.
 *
 * Absolute URLs (http/https) are passed through untouched.
 */
export function getAuthToken(): string {
  return (
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    ''
  );
}

export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const base = API_BASE_URL.replace(/\/$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getAuthToken();
  const headers = new Headers(init.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(apiUrl(path), { ...init, headers });
}

export default apiFetch;
